import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { Input } from 'components/Input';

type QuantityCellProps = {
  rowId: string;
  initialValue: number;
  isDisabled: boolean;
  onQuantityChange: (rowId: string, quantity: string) => void;
};

/**
 * Memoized cell component that only re-renders when its own props change.
 * This prevents unnecessary re-renders when other cells in the table update.
 */
export const QuantityCell = memo<QuantityCellProps>(
  ({ rowId, initialValue, isDisabled, onQuantityChange }) => {
    // Local state for immediate UI feedback
    const [localValue, setLocalValue] = useState<string | number>(initialValue);
    const previousInitialValue = useRef(initialValue);

    // Sync with external changes (e.g., from API refresh)
    useEffect(() => {
      if (initialValue !== previousInitialValue.current) {
        setLocalValue(initialValue);
        previousInitialValue.current = initialValue;
      }
    }, [initialValue]);

    const handleChange = useCallback((newValue: string | number) => {
      setLocalValue(newValue);
    }, []);

    const handleBlur = useCallback(
      (value: string) => {
        const normalizedValue = value.trim() === '' ? '0' : value;
        setLocalValue(normalizedValue);
        onQuantityChange(rowId, normalizedValue);
      },
      [rowId, onQuantityChange]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const normalizedValue =
            e.currentTarget.value.trim() === '' ? '0' : e.currentTarget.value;
          setLocalValue(normalizedValue);
          onQuantityChange(rowId, normalizedValue);
          e.currentTarget.blur();
        }
      },
      [rowId, onQuantityChange]
    );

    return (
      <Input
        disabled={isDisabled}
        name={`currQuantity-${rowId}`}
        // type="number"
        min="0"
        step="1"
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={(e) => handleBlur(e.target.value)}
        onKeyDown={handleKeyDown}
        style={isDisabled ? { cursor: 'not-allowed' } : undefined}
      />
    );
  },
  // Custom comparison function for memo
  (prevProps, nextProps) =>
    prevProps.rowId === nextProps.rowId &&
    prevProps.initialValue === nextProps.initialValue &&
    prevProps.isDisabled === nextProps.isDisabled &&
    prevProps.onQuantityChange === nextProps.onQuantityChange
);

QuantityCell.displayName = 'QuantityCell';
